import React, { useState, useEffect } from 'react';
import { Card, Select, Button, Row, Col, Statistic, message, Space, Tabs } from 'antd';
import { DownloadOutlined, ReloadOutlined, BarChartOutlined } from '@ant-design/icons';
import WaterResourceChart from './WaterResourceChart';
import WaterResourceModelIntro from './WaterResourceModelIntro';
import {
  getAvailableConfigurations,
  getAvailableYearsForRegion,
  loadComparisonData,
  formatDataForChart,
  calculateComparisonStats,
  exportComparisonData
} from '../utils/waterResourceDataUtils';

const { Option } = Select;

const WaterResourcePage = () => {
  // 状态管理
  const [loading, setLoading] = useState(false);
  const [chartData, setChartData] = useState(null);
  const [comparisonData, setComparisonData] = useState([]);
  const [statistics, setStatistics] = useState(null);
  
  // 选择器状态
  const [selectedYear, setSelectedYear] = useState(2020);
  const [selectedRegion, setSelectedRegion] = useState('七城区四产业');
  const [availableYears, setAvailableYears] = useState([2020, 2024]);
  
  // 配置数据
  const [configurations] = useState(getAvailableConfigurations());
  const [hasInitialLoaded, setHasInitialLoaded] = useState(false);

  // 组件挂载后立即加载数据
  useEffect(() => {
    console.log('组件挂载，开始初始化数据加载...');
    loadData();
    setHasInitialLoaded(true);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 当选择器变化时加载数据（排除初始加载）
  useEffect(() => {
    if (hasInitialLoaded) {
      console.log(`数据配置变更: 年份=${selectedYear}, 区域=${selectedRegion}`);
      loadData();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedYear, selectedRegion]);

  /**
   * 加载数据
   */
  const loadData = async () => {
    setLoading(true);
    // 清空旧数据，避免显示过期内容
    setChartData(null);
    
    try {
      console.log(`开始加载数据: 年份=${selectedYear}, 区域=${selectedRegion}`);
      
      // 加载对比数据
      const data = await loadComparisonData(selectedYear, selectedRegion);
      
      if (!data || data.length === 0) {
        console.warn('未获取到数据');
        setComparisonData([]);
        setChartData(null);
        setStatistics(null);
        message.warning('未找到相关数据');
        return;
      }
      
      setComparisonData(data);
      
      // 格式化图表数据
      const formattedChartData = formatDataForChart(data);
      
      // 验证图表数据的完整性
      if (formattedChartData &&
          formattedChartData.categories &&
          formattedChartData.categories.length > 0 &&
          formattedChartData.series &&
          formattedChartData.series.length > 0 &&
          formattedChartData.series.every(s => s.data && s.data.length > 0)) {
        console.log('图表数据验证通过:', formattedChartData);
        setChartData(formattedChartData);
      } else {
        console.error('图表数据格式不正确:', formattedChartData);
        setChartData(null);
        message.error('数据格式错误，无法显示图表');
      }
      
      // 计算统计信息
      const stats = calculateComparisonStats(data);
      setStatistics(stats);
      
      message.success(`数据加载成功，共 ${data.length} 条记录`);
    } catch (error) {
      console.error('数据加载失败:', error);
      message.error(`数据加载失败: ${error.message}`);
      setChartData(null);
      setComparisonData([]);
      setStatistics(null);
    } finally {
      setLoading(false);
    }
  };

  /**
   * 处理年份变更
   */
  const handleYearChange = (year) => {
    setSelectedYear(year);
  };

  /**
   * 处理区域变更
   */
  const handleRegionChange = (region) => {
    setSelectedRegion(region);
    
    // 更新可用年份
    const years = getAvailableYearsForRegion(region);
    setAvailableYears(years);
    
    // 如果当前选择的年份不在新区域的可用年份中，选择第一个可用年份
    if (!years.includes(selectedYear)) {
      setSelectedYear(years[0]);
    }
  };

  /**
   * 刷新数据
   */
  const handleRefresh = () => {
    loadData();
  };

  /**
   * 导出数据
   */
  const handleExport = () => {
    if (comparisonData && comparisonData.length > 0) {
      const fileName = `water_resource_comparison_${selectedRegion}_${selectedYear}`;
      exportComparisonData(comparisonData, fileName);
      message.success('数据导出成功');
    } else {
      message.warning('暂无数据可导出');
    }
  };

  const tabItems = [
    {
      key: '1',
      label: '数据分析',
      children: (
        <>
          {/* 控制面板 */}
          <Card style={{ marginBottom: '24px' }}>
            <Row gutter={[16, 16]} align="middle">
              <Col xs={24} sm={12} md={6} lg={4}>
                <div style={{ marginBottom: '8px' }}>
                  <label style={{ fontSize: '14px', fontWeight: '500', color: '#333' }}>
                    选择年份:
                  </label>
                </div>
                <Select
                  value={selectedYear}
                  onChange={handleYearChange}
                  style={{ width: '100%' }}
                  size="large"
                >
                  {availableYears.map(year => (
                    <Option key={year} value={year}>
                      {year}年
                    </Option>
                  ))}
                </Select>
              </Col>
              
              <Col xs={24} sm={12} md={8} lg={6}>
                <div style={{ marginBottom: '8px' }}>
                  <label style={{ fontSize: '14px', fontWeight: '500', color: '#333' }}>
                    选择区域:
                  </label>
                </div>
                <Select
                  value={selectedRegion}
                  onChange={handleRegionChange}
                  style={{ width: '100%' }}
                  size="large"
                >
                  {configurations.regions.map(region => (
                    <Option key={region.key} value={region.key}>
                      {region.label}
                    </Option>
                  ))}
                </Select>
              </Col>

              <Col xs={24} sm={24} md={8} lg={6}>
                <div style={{ marginBottom: '8px' }}>
                  <label style={{ fontSize: '14px', fontWeight: '500', color: 'transparent' }}>
                    操作:
                  </label>
                </div>
                <Space>
                  <Button
                    icon={<ReloadOutlined />}
                    onClick={handleRefresh}
                    loading={loading}
                    size="large"
                  >
                    刷新
                  </Button>
                  <Button
                    type="primary"
                    icon={<DownloadOutlined />}
                    onClick={handleExport}
                    disabled={!comparisonData || comparisonData.length === 0}
                    size="large"
                  >
                    导出
                  </Button>
                </Space>
              </Col>
            </Row>
          </Card>

          {/* 统计信息面板 */}
          {statistics && (
            <Card style={{ marginBottom: '24px' }}>
              <Row gutter={[16, 16]}>
                <Col xs={12} sm={6} md={4}>
                  <Statistic
                    title="优化用水总量"
                    value={statistics.totalOptimized}
                    suffix="亿m³"
                    valueStyle={{ color: '#1890ff' }}
                  />
                </Col>
                <Col xs={12} sm={6} md={4}>
                  <Statistic
                    title="实际用水总量"
                    value={statistics.totalActual}
                    suffix="亿m³"
                    valueStyle={{ color: '#ff7875' }}
                  />
                </Col>
                <Col xs={12} sm={6} md={4}>
                  <Statistic
                    title="差值"
                    value={statistics.difference}
                    suffix="亿m³"
                    valueStyle={{ 
                      color: parseFloat(statistics.difference) >= 0 ? '#ff4d4f' : '#52c41a' 
                    }}
                    prefix={parseFloat(statistics.difference) >= 0 ? '+' : ''}
                  />
                </Col>
                <Col xs={12} sm={6} md={4}>
                  <Statistic
                    title="优化率"
                    value={statistics.efficiencyRate}
                    suffix="%"
                    valueStyle={{ color: '#52c41a' }}
                  />
                </Col>
                <Col xs={12} sm={6} md={4}>
                  <Statistic
                    title="数据点数"
                    value={statistics.count}
                    suffix="个"
                    valueStyle={{ color: '#722ed1' }}
                  />
                </Col>
              </Row>
            </Card>
          )}

          {/* 图表展示 - 移除key属性以避免重新挂载 */}
          <WaterResourceChart
            data={chartData}
            loading={loading}
            title="优化用水 vs 实际用水对比"
            year={selectedYear}
            region={selectedRegion}
          />

          {/* 使用说明 */}
          <Card title="使用说明" size="small" style={{ marginTop: '24px' }}>
            <Row gutter={[16, 16]}>
              <Col xs={24} md={12}>
                <h4>数据说明:</h4>
                <ul style={{ paddingLeft: '20px', color: '#666' }}>
                  <li>优化用水：基于数学模型计算的最优用水配置</li>
                  <li>实际用水：基于统计数据的实际用水量</li>
                  <li>差值：实际用水量减去优化用水量</li>
                  <li>优化率：优化用水量占实际用水量的百分比</li>
                </ul>
              </Col>
              <Col xs={24} md={12}>
                <h4>操作说明:</h4>
                <ul style={{ paddingLeft: '20px', color: '#666' }}>
                  <li>选择不同年份和区域查看对比数据</li>
                  <li>点击图例可切换显示/隐藏数据系列</li>
                  <li>使用工具箱功能进行缩放、保存等操作</li>
                  <li>支持导出当前数据为CSV格式</li>
                </ul>
              </Col>
            </Row>
          </Card>
        </>
      )
    },
    {
      key: '2',
      label: '模型介绍',
      children: <WaterResourceModelIntro />
    }
  ];

  return (
    <div style={{ padding: '24px' }}>
      {/* 页面标题 */}
      <div style={{ marginBottom: '24px' }}>
        <h1 style={{ 
          fontSize: '24px', 
          fontWeight: 'bold', 
          margin: 0,
          color: '#1890ff',
          display: 'flex',
          alignItems: 'center'
        }}>
          <BarChartOutlined style={{ marginRight: '12px' }} />
          东江流域及珠江三角洲水资源配置分析
        </h1>
        <p style={{ 
          color: '#666', 
          margin: '8px 0 0 36px',
          fontSize: '14px'
        }}>
          对比分析不同区域、年份的优化用水与实际用水情况
        </p>
      </div>

      {/* 标签页 */}
      <Tabs defaultActiveKey="1" items={tabItems} />
    </div>
  );
};

export default WaterResourcePage;